import { useEffect, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type {
  FoundryCatalogModel,
  FoundryModel,
  FoundryModelExcludeEntry,
  FoundryModelPickerUiOptions,
} from './schema';
import { useFoundryModels } from './useFoundryModels';

export type { FoundryModelPickerSchema } from './schema';

type Props = FieldExtensionComponentProps<
  FoundryModel | undefined,
  FoundryModelPickerUiOptions
>;

function excludeFromCatalog(
  exclude: FoundryModelExcludeEntry[],
  catalog: FoundryCatalogModel[],
): FoundryCatalogModel[] {
  const excludeByName = new Map<string, Set<string>>();

  for (const entry of exclude) {
    const versions = entry.versions ?? [];
    const existing = excludeByName.get(entry.name);

    // Empty versions on any duplicate entry excludes the whole model.
    if (versions.length === 0) {
      excludeByName.set(entry.name, new Set());
      continue;
    }

    if (existing?.size === 0) {
      continue;
    }

    const merged = existing ?? new Set<string>();
    for (const version of versions) {
      merged.add(version);
    }
    excludeByName.set(entry.name, merged);
  }

  return catalog
    .map(model => {
      const excludedVersions = excludeByName.get(model.name);
      if (!excludedVersions) {
        return model;
      }
      if (excludedVersions.size === 0) {
        return undefined;
      }
      const versions = model.versions.filter(
        version => !excludedVersions.has(version),
      );
      if (versions.length === 0) {
        return undefined;
      }
      return { name: model.name, versions };
    })
    .filter((entry): entry is FoundryCatalogModel => Boolean(entry));
}

function isCompleteModel(
  value: FoundryModel | undefined,
): value is FoundryModel {
  return Boolean(value?.name && value.version);
}

function isEmptyModel(value: FoundryModel | undefined): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  return !value.name && !value.version;
}

export const validateFoundryModel = (
  value: FoundryModel | undefined,
  validation: { addError: (message: string) => void },
) => {
  // RJSF seeds nested object fields as {} when the parent array has minItems.
  // Treat empty objects as "not started" so required-field handling owns that case.
  if (isEmptyModel(value)) {
    return;
  }
  if (!isCompleteModel(value)) {
    validation.addError('Select both a model name and a version');
  }
};

export const FoundryModelPicker = (props: Props) => {
  const {
    onChange,
    schema: {
      title = 'Foundry model',
      description = 'Select a model name and version',
    },
    required,
    uiSchema,
    rawErrors,
    formData,
  } = props;

  const excludeOption = uiSchema['ui:options']?.excludeModels;
  const hasExclusions =
    Array.isArray(excludeOption) && excludeOption.length > 0;
  const { catalogModels, loading, error } = useFoundryModels(true);

  // Omit or empty ui:options.excludeModels → full catalog.
  const options = hasExclusions
    ? excludeFromCatalog(excludeOption, catalogModels)
    : catalogModels;
  const nameOptions = options.map(model => model.name);
  const [selectedName, setSelectedName] = useState<string | null>(
    formData?.name ?? null,
  );
  const [selectedVersion, setSelectedVersion] = useState<string | null>(
    formData?.version || null,
  );

  useEffect(() => {
    if (formData?.name) {
      setSelectedName(formData.name);
      setSelectedVersion(formData.version || null);
    }
  }, [formData]);

  const versionOptions =
    options.find(model => model.name === selectedName)?.versions ?? [];

  const handleNameChange = (_: React.ChangeEvent<{}>, value: string | null) => {
    setSelectedName(value);
    setSelectedVersion(null);
    onChange(value ? { name: value, version: '' } : undefined);
  };

  const handleVersionChange = (
    _: React.ChangeEvent<{}>,
    value: string | null,
  ) => {
    setSelectedVersion(value);
    if (!selectedName) {
      onChange(undefined);
      return;
    }
    onChange({ name: selectedName, version: value ?? '' });
  };

  const helperText = error ?? (rawErrors?.length ? rawErrors[0] : description);

  return (
    <Box>
      <Autocomplete
        options={nameOptions}
        getOptionLabel={option => option}
        getOptionSelected={(option, value) => option === value}
        value={
          selectedName && nameOptions.includes(selectedName)
            ? selectedName
            : null
        }
        onChange={handleNameChange}
        loading={loading}
        disableClearable={false}
        freeSolo={false}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            helperText={helperText}
            error={!!rawErrors?.length || !!error}
            required={required}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
      />
      <Box mt={2}>
        <Autocomplete
          options={versionOptions}
          getOptionLabel={option => option}
          getOptionSelected={(option, value) => option === value}
          value={
            selectedVersion && versionOptions.includes(selectedVersion)
              ? selectedVersion
              : null
          }
          onChange={handleVersionChange}
          loading={loading}
          disabled={!selectedName}
          disableClearable={false}
          freeSolo={false}
          renderInput={params => (
            <TextField
              {...params}
              label="Model version"
              helperText={
                selectedName
                  ? 'Select a version for the chosen model'
                  : 'Select a model name first'
              }
              required={required}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};
